import { useMemo } from 'react';
import { Tag } from 'antd';
import { useSelector } from 'react-redux';
import { getLang } from 'shared/store/selectors/lang.selector';
import { formatNumber } from 'shared/utils';
import { AG_FILTER_TYPES, textWithFallback } from 'src/components/design-system/table';
import { attendanceModeLabel, eventDetailsPath } from '../../helpers/event-view.helpers';
import { useEducationalEventFormat } from '../../helpers/use-educational-event-format';
import { ReportTable, SET_FILTER, RESETTABLE_FILTER } from './report-table';
import { ReportLinkCell } from './report-link-cell';
import styles from './reports.module.scss';

export function ByEventReport({ rows, onSelectEvent }) {
  const lang = useSelector(getLang('EDUCATIONAL_EVENTS'));
  const { formatDateWithTime } = useEducationalEventFormat();

  const columns = useMemo(
    () => [
      {
        headerName: lang.REPORT_COL_EVENT,
        colId: 'title',
        field: 'title',
        sortable: true,
        flex: 2,
        cellClass: styles.primaryCell,
        valueFormatter: textWithFallback,
        cellRenderer: ReportLinkCell,
        cellRendererParams: {
          getTo: data =>
            data?.eventId ? eventDetailsPath(data.eventId, { fromReport: true }) : null,
        },
        filter: AG_FILTER_TYPES.TEXT,
        filterParams: RESETTABLE_FILTER,
        comparator: (a, b) => (a || '').toLowerCase().localeCompare((b || '').toLowerCase()),
      },
      {
        headerName: lang.REPORT_COL_DATE,
        colId: 'startTime',
        sortable: true,
        sort: 'desc',
        flex: 1,
        valueGetter: params => params.data?.startTime ?? null,
        cellRenderer: params =>
          params.value ? formatDateWithTime(params.value) : textWithFallback({ value: null }),
        filter: AG_FILTER_TYPES.DATE,
        filterParams: RESETTABLE_FILTER,
        filterValueGetter: params =>
          params.data?.startTime ? new Date(params.data.startTime) : null,
        comparator: (a, b) => new Date(a || 0).getTime() - new Date(b || 0).getTime(),
      },
      {
        headerName: lang.REPORT_COL_TYPE,
        colId: 'eventAttendanceMode',
        sortable: true,
        flex: 1,
        valueGetter: params => params.data?.eventAttendanceMode,
        cellRenderer: params =>
          params.value ? (
            <Tag className={styles.typeTag} variant="filled">
              {attendanceModeLabel(params.value, lang)}
            </Tag>
          ) : (
            '-'
          ),
        filter: SET_FILTER,
        filterParams: { valueFormatter: value => attendanceModeLabel(value, lang) },
      },
      {
        headerName: lang.REPORT_COL_ATTENDEES,
        colId: 'attendeesCount',
        sortable: true,
        flex: 1,
        valueGetter: params => params.data?.attendeesCount ?? 0,
        cellRenderer: params => formatNumber(params.value ?? 0),
        filter: AG_FILTER_TYPES.NUMBER,
        filterParams: RESETTABLE_FILTER,
      },
    ],
    [lang, formatDateWithTime],
  );

  return (
    <ReportTable
      columnDefs={columns}
      rowData={rows}
      getRowId={params => params.data.eventId}
      onRowActivate={data => data?.eventId && onSelectEvent(data.eventId)}
    />
  );
}
